import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { 
  FileText, 
  Download, 
  RefreshCw,
  Wallet,
  TrendingUp,
  Eye,
  EyeOff,
  X,
  Mail,
  Send,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyPayslipsPage = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('omnilink_token');
  
  const [loading, setLoading] = useState(false);
  const [payslips, setPayslips] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [viewPayroll, setViewPayroll] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  
  // Privacy toggle - stats hidden by default
  const [showStats, setShowStats] = useState(false);
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailPayroll, setEmailPayroll] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const fetchPayslips = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/payroll/my-payslips?year=${selectedYear}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setPayslips(data.payslips || []);
    } catch (error) {
      console.error('Error fetching payslips:', error);
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear]);

  useEffect(() => {
    fetchPayslips();
  }, [fetchPayslips]);

  const handleDownloadSlip = async (payrollId, month, year) => {
    try {
      const response = await fetch(`${API_URL}/api/payroll/${payrollId}/slip`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      a.download = `Payslip_${monthNames[month]}_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Payslip downloaded');
    } catch (error) {
      toast.error('Failed to download payslip');
    }
  };

  const handleEmailPayslip = async () => {
    if (!recipientEmail.trim()) {
      toast.error('Please enter a recipient email');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(recipientEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    try {
      setSendingEmail(true);
      const response = await fetch(`${API_URL}/api/payroll/${emailPayroll.id}/email`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ recipient_email: recipientEmail })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send email');
      }
      
      toast.success(`Payslip sent to ${recipientEmail}`);
      setShowEmailModal(false);
      setRecipientEmail('');
      setEmailPayroll(null);
    } catch (error) {
      toast.error(error.message || 'Failed to send payslip');
    } finally {
      setSendingEmail(false);
    }
  };

  const openEmailModal = (payslip) => {
    setEmailPayroll(payslip);
    setRecipientEmail('');
    setShowEmailModal(true);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];

  // Calculate total earnings this year
  const totalEarnings = payslips.reduce((sum, p) => sum + (p.net_payable || 0), 0);
  const totalIncentives = payslips.reduce((sum, p) => sum + (p.incentive || 0), 0);

  // Hidden value display
  const hiddenValue = '••••••';

  return (
    <div className="space-y-6" data-testid="my-payslips-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Payslips</h1>
          <p className="text-gray-500 mt-1">View and download your salary slips</p>
        </div>
        <Select value={`${selectedYear}`} onValueChange={(v) => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026, 2027].map(y => (
              <SelectItem key={y} value={`${y}`}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards with Privacy Toggle */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Earnings ({selectedYear})</p>
                <p className="text-2xl font-bold text-green-600">
                  {showStats ? formatCurrency(totalEarnings) : hiddenValue}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Wallet className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Incentives</p>
                <p className="text-2xl font-bold text-blue-600">
                  {showStats ? formatCurrency(totalIncentives) : hiddenValue}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Payslips Available</p>
                <p className="text-2xl font-bold text-purple-600">{payslips.length}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowStats(!showStats)}
                className="p-2 hover:bg-purple-100"
                data-testid="privacy-toggle-btn"
              >
                {showStats ? (
                  <EyeOff className="h-5 w-5 text-purple-600" />
                ) : (
                  <Eye className="h-5 w-5 text-purple-600" />
                )}
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              {showStats ? 'Click eye to hide amounts' : 'Click eye to show amounts'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Payslips List */}
      <Card>
        <CardHeader>
          <CardTitle>Payslips - {selectedYear}</CardTitle>
          <CardDescription>Your monthly salary statements</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : payslips.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="font-medium">No payslips available</p>
              <p className="text-sm">Your payslips will appear here once generated</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {payslips.map(payslip => (
                <Card key={payslip.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-lg">
                          {monthNames[payslip.month - 1]} {payslip.year}
                        </h3>
                        <p className="text-xs text-gray-500">
                          Paid Days: {payslip.paid_days} / {payslip.total_days}
                        </p>
                      </div>
                      <Badge 
                        variant="outline"
                        className={payslip.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}
                      >
                        {payslip.status}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 text-sm border-t pt-3">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Gross Earnings</span>
                        <span>{showStats ? formatCurrency(payslip.gross_earnings) : hiddenValue}</span>
                      </div>
                      {payslip.incentive > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Incentive</span>
                          <span className="text-green-600">
                            {showStats ? `+${formatCurrency(payslip.incentive)}` : hiddenValue}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Deductions</span>
                        <span className="text-red-600">
                          {showStats ? `-${formatCurrency(payslip.total_deductions)}` : hiddenValue}
                        </span>
                      </div>
                      <div className="flex justify-between font-semibold border-t pt-2">
                        <span>Net Payable</span>
                        <span className="text-lg text-indigo-700">
                          {showStats ? formatCurrency(payslip.net_payable) : hiddenValue}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => { setViewPayroll(payslip); setShowViewModal(true); }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => openEmailModal(payslip)}
                        data-testid={`email-btn-${payslip.id}`}
                      >
                        <Mail className="h-4 w-4 mr-1" />
                        Email
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => handleDownloadSlip(payslip.id, payslip.month, payslip.year)}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Details Modal */}
      {showViewModal && viewPayroll && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Payslip Details</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowViewModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                {monthNames[viewPayroll.month - 1]} {viewPayroll.year}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Employee Code</p>
                  <p className="font-medium">{viewPayroll.employee_code}</p>
                </div>
                <div>
                  <p className="text-gray-500">Designation</p>
                  <p className="font-medium">{viewPayroll.designation}</p>
                </div>
                <div>
                  <p className="text-gray-500">Paid Days</p>
                  <p className="font-medium">{viewPayroll.paid_days} / {viewPayroll.total_days}</p>
                </div>
                <div>
                  <p className="text-gray-500">LOP Days</p>
                  <p className="font-medium">{viewPayroll.lop_days}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <p className="font-medium mb-3 text-green-700">Earnings</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Basic</span>
                    <span>{formatCurrency(viewPayroll.basic)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>House Rent Allowance</span>
                    <span>{formatCurrency(viewPayroll.hra)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Conveyance Allowance</span>
                    <span>{formatCurrency(viewPayroll.conveyance)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Performance Incentive</span>
                    <span className="text-green-600">{formatCurrency(viewPayroll.incentive)}</span>
                  </div>
                  <div className="flex justify-between font-medium border-t pt-2">
                    <span>Gross Earnings</span>
                    <span>{formatCurrency(viewPayroll.gross_earnings)}</span>
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <p className="font-medium mb-3 text-red-700">Deductions</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Professional Tax</span>
                    <span>{formatCurrency(viewPayroll.professional_tax)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Loan/Advance</span>
                    <span>{formatCurrency(viewPayroll.loan_deduction)}</span>
                  </div>
                  <div className="flex justify-between font-medium border-t pt-2">
                    <span>Total Deductions</span>
                    <span className="text-red-600">{formatCurrency(viewPayroll.total_deductions)}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-indigo-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-lg">Net Payable</span>
                  <span className="font-bold text-2xl text-indigo-700">{formatCurrency(viewPayroll.net_payable)}</span>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button 
                  variant="outline"
                  onClick={() => { setShowViewModal(false); openEmailModal(viewPayroll); }}
                  className="gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Email
                </Button>
                <Button 
                  onClick={() => handleDownloadSlip(viewPayroll.id, viewPayroll.month, viewPayroll.year)}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download Payslip
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Email Payslip Modal */}
      {showEmailModal && emailPayroll && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-indigo-600" />
                  Email Payslip
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowEmailModal(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>
                Send {monthNames[emailPayroll.month - 1]} {emailPayroll.year} payslip via email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <div className="flex justify-between mb-1">
                  <span className="text-gray-500">From:</span>
                  <span className="font-medium">{user?.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Attachment:</span>
                  <span className="font-medium text-indigo-600">
                    Payslip_{monthNames[emailPayroll.month - 1]}_{emailPayroll.year}.pdf
                  </span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient Email Address
                </label>
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  data-testid="recipient-email-input"
                  className="w-full"
                  disabled={sendingEmail}
                />
                <p className="text-xs text-gray-500 mt-1">
                  The payslip will be sent from your email ({user?.email})
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowEmailModal(false)}
                  disabled={sendingEmail}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleEmailPayslip}
                  disabled={sendingEmail || !recipientEmail.trim()}
                  data-testid="send-email-btn"
                  className="gap-2"
                >
                  {sendingEmail ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send Payslip
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MyPayslipsPage;
